import { FieldKind, type DocField, type FieldOption, type FieldValue } from './field-kind';
import { W14_NS, W_NS, attr, descendants, firstKid, kids } from './ns';
import { isChecked } from './checkbox-glyph';
import { DEFAULT_DATE_FORMAT, isoFromFullDate, parseDate } from './date-format';

export const PLACEHOLDER_STYLE = 'PlaceholderText';

export interface FieldBinding {
  readonly field: DocField;
  readonly sdt: Element;
  readonly content: Element;
}

export function readFields(part: Document): FieldBinding[] {
  const bindings: FieldBinding[] = [];

  for (const sdt of descendants(part.documentElement, 'sdt')) {
    const content = firstKid(sdt, 'sdtContent');
    if (!content || hasNestedSdt(content)) {
      continue;
    }

    bindings.push(readOne(sdt, content, bindings.length + 1));
  }

  return bindings;
}

function hasNestedSdt(content: Element): boolean {
  return content.getElementsByTagNameNS(W_NS, 'sdt').length > 0;
}

function readOne(sdt: Element, content: Element, ordinal: number): FieldBinding {
  const props = firstKid(sdt, 'sdtPr');
  const checkbox = props ? firstKid(props, 'checkbox', W14_NS) : undefined;
  const kind = classify(props, checkbox);

  const text = contentText(content);
  const named = props
    ? attr(firstKid(props, 'alias'), 'val') || attr(firstKid(props, 'tag'), 'val')
    : undefined;
  const isPlaceholder = !!props && !!firstKid(props, 'showingPlcHdr');

  return {
    sdt,
    content,
    field: {
      id: fieldId(props, ordinal),
      kind,
      label: named || text.trim() || `Field ${ordinal}`,
      value: valueOf(kind, props, checkbox, text, isPlaceholder),
      placeholder: hintText(kind, text, isPlaceholder, !!named),
      options:
        kind === FieldKind.DropDown || kind === FieldKind.ComboBox ? options(props!, kind) : [],
      isPlaceholder,
      multiline: kids(content, 'p').length > 0,
      ...(kind === FieldKind.Date ? { dateFormat: dateFormatOf(props!) } : {}),
    },
  };
}

function dateFormatOf(props: Element): string {
  const date = firstKid(props, 'date');

  return attr(date && firstKid(date, 'dateFormat'), 'val') || DEFAULT_DATE_FORMAT;
}

function valueOf(
  kind: FieldKind,
  props: Element | undefined,
  checkbox: Element | undefined,
  text: string,
  isPlaceholder: boolean,
): FieldValue {
  if (kind === FieldKind.CheckBox) {
    return isChecked(checkbox!);
  }

  if (isPlaceholder) {
    return '';
  }

  if (kind === FieldKind.Date) {
    return dateValue(props!, text) ?? text;
  }

  return text;
}

// A date picker's value is the date itself (yyyy-mm-dd): Word keeps it in
// w:fullDate. Without one, the text is read in the picker's own format. Text that is
// not a date, such as "Onbekend", stays the value as it is.
function dateValue(props: Element, text: string): string | undefined {
  const date = firstKid(props, 'date');

  return isoFromFullDate(attr(date, 'fullDate')) ?? parseDate(text, dateFormatOf(props));
}

function hintText(kind: FieldKind, text: string, isPlaceholder: boolean, named: boolean): string {
  if (kind === FieldKind.CheckBox) {
    return '';
  }

  return isPlaceholder || !named ? text.trim() : '';
}

function classify(props: Element | undefined, checkbox: Element | undefined): FieldKind {
  if (!props) {
    return FieldKind.RichText;
  }

  if (checkbox) {
    return FieldKind.CheckBox;
  }

  if (firstKid(props, 'dropDownList')) {
    return FieldKind.DropDown;
  }

  if (firstKid(props, 'comboBox')) {
    return FieldKind.ComboBox;
  }

  if (firstKid(props, 'date')) {
    return FieldKind.Date;
  }

  if (firstKid(props, 'text')) {
    return FieldKind.Text;
  }

  return FieldKind.RichText;
}

function fieldId(props: Element | undefined, ordinal: number): string {
  const id = props ? attr(firstKid(props, 'id'), 'val') : undefined;

  return `fld-${id ?? ordinal}`;
}

function options(props: Element, kind: FieldKind): FieldOption[] {
  const list = firstKid(props, kind === FieldKind.DropDown ? 'dropDownList' : 'comboBox');
  if (!list) {
    return [];
  }

  return kids(list, 'listItem').map((item) => {
    const value = attr(item, 'value') ?? '';

    return { value, label: attr(item, 'displayText') ?? value };
  });
}

function contentText(content: Element): string {
  const paragraphs = kids(content, 'p');
  if (paragraphs.length > 0) {
    return paragraphs.map(runText).join('\n');
  }

  return runText(content);
}

function runText(scope: Element): string {
  return descendants(scope, 't')
    .map((t) => t.textContent ?? '')
    .join('');
}
